import React, {PureComponent} from 'react';
import {render} from 'react-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';

import StateOutline from "../MapElements/StateOutline";
import StateCountyBorders from "../MapElements/StateCountyBorders";
import StateCountyShapes from "../MapElements/StateCountyShapes";
import StateCityLabels from "../MapElements/StateCityLabels";
import PatternCrosshatch from "../MapElements/PatternCrosshatch";
import LegendGroup from "../Legend/LegendGroup";

import {TEXT} from "../../../utilities/strings";

import * as Utils from "../../../utilities/utilities";
import {VARIABLES} from "../../../utilities/constants";


/**
 * Component for rendering the state map at the county level for export.
 *
 * This component was created with the sole intent of rendering an html
 * svg chart element offscreen and extracting the svg text for export.
 *
 * @example
 * const svgText = StateMapExport.renderSvgText(selectedCost, selectedState, countyData, 850, 750)
 */
export default class StateMapExport extends PureComponent {

  static propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    selectedCost: PropTypes.string,
    selectedState: PropTypes.object,
    countyData: PropTypes.array
  }

  /**
   * Static function allows the creation and rendering of this map
   * programmatically offscreen for export.
   *
   * @param selectedCost - selected cost value
   * @param selectedState - selected state location data object
   * @param countyData - array of county location data
   * @param width - pixel width to render
   * @param height - pixel height to render
   * @returns {string} - serialized svg text
   */
  static renderSvgText = (selectedCost, selectedState, countyData, width, height) => {
    const element = document.createElement('div');
    render(
      <StateMapExport
        selectedCost={selectedCost}
        selectedState={selectedState}
        countyData={countyData}
        width={width}
        height={height}
      />
      , element);

    const svg = element.querySelector('svg');
    return new XMLSerializer().serializeToString(svg)
  }

  render() {
    const {width, height, selectedCost, selectedState, countyData} = this.props;
    const mapHeight = width * .57

    const dict = _.keyBy(countyData, VARIABLES.CountyID)

    const stateName = _.get(selectedState, VARIABLES.Location, 'State')
    const stateFips = _.get(selectedState, VARIABLES.StateID, '')

    const exportTitle = stateName + ' ' + Utils.getCountyExportTitle(selectedCost);
    const exportFootnote = TEXT.county_export_footnote;

    const colorField = Utils.getMapValueProperty(selectedCost);
    const legendField = Utils.getLegendValueProperty(selectedCost);

    return (
      <svg width={width} height={height}>
        <PatternCrosshatch/>

        <rect width={width} height={height} fill="#fff"/>

        <text x={width / 2} y={15} fontWeight="bold" fontFamily="arial,sans-serif" fontSize={17} textAnchor="middle">
          {exportTitle}
        </text>

        <g transform={`translate(0,40)`}>
          <StateOutline
            stateFips={stateFips}
            width={width}
            height={mapHeight}
          />
          <StateCountyShapes
            stateFips={stateFips}
            data={countyData}
            dict={dict}
            width={width}
            height={mapHeight}
            dimension={colorField}
          />
          <StateCountyBorders
            stateFips={stateFips}
            width={width}
            height={mapHeight}
          />
          <StateCityLabels
            stateFips={stateFips}
            width={width}
            height={mapHeight}
          />
        </g>
        <g transform={`translate(10,${mapHeight + 60})`}>
          <LegendGroup
            data={countyData}
            height={mapHeight}
            width={width - 10}
            title={exportTitle}
            footnote={exportFootnote}
            quantileField={colorField}
            valueField={legendField}
          />
        </g>
      </svg>
    );
  }
}
